import { useSyncExternalStore, useMemo, useRef } from 'react';

const EMPTY = Object.freeze([]);

// Subscribes to a y-protocols/awareness instance and returns an array of
// remote member states (excluding the local client).
//
// Pass the result of getDiagramAwareness() — when no diagram is open
// (awareness is null) the hook returns a frozen empty array.
export function useAwarenessMembers(awareness) {
    const cacheRef = useRef({ awareness: null, snapshot: EMPTY, dirty: true });

    const { subscribe, getSnapshot } = useMemo(() => {
        cacheRef.current = { awareness, snapshot: EMPTY, dirty: true };

        const subscribe = (listener) => {
            if (!awareness) return () => {};
            const cb = () => {
                cacheRef.current.dirty = true;
                listener();
            };
            awareness.on('change', cb);
            return () => awareness.off('change', cb);
        };

        const getSnapshot = () => {
            if (!awareness) return EMPTY;
            if (!cacheRef.current.dirty) return cacheRef.current.snapshot;
            const states = awareness.getStates();
            const localClientId = awareness.clientID;
            // Also skip any state belonging to the same user account (other open
            // tabs of the same user have different clientIDs but the same user.id).
            const localUserId = awareness.getLocalState()?.user?.id;
            // Deduplicate by user.id — the server may echo awareness updates back,
            // or the same user may have multiple tabs open.
            const seen = new Map(); // userId → state
            states.forEach((state, clientId) => {
                if (clientId === localClientId) return;
                if (!state) return;
                const userId = state.user?.id;
                if (localUserId && userId === localUserId) return;
                if (userId) {
                    seen.set(userId, state);
                } else {
                    seen.set(clientId, state);
                }
            });
            const out = Array.from(seen.values());
            cacheRef.current.snapshot = out.length ? out : EMPTY;
            cacheRef.current.dirty = false;
            return cacheRef.current.snapshot;
        };

        return { subscribe, getSnapshot };
    }, [awareness]); // eslint-disable-line

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
